import { SUITS, SEATS, seatTeam } from "./setbackRules";
import { determinizeHands, shuffle, unseenPool } from "./determinize";
import { playOutHand } from "./simulate";
import { chooseHeuristicCard } from "./rolloutPolicy";

const BID_ITERATIONS = 40;

// Estimates the expected point swing (my team's points minus the other
// team's) if `mySeat` wins the bid at `level` with `trump` as the trump
// suit, by sampling opponent hands and playing full hands out with the fast
// heuristic policy for everyone (including myself) - a cheap enough
// evaluation to run across every (level, trump) candidate.
//
// `remainingPools`, when given (see chooseBid below), is an array of
// pre-shuffled unseenPool() draws - one per iteration - shared across every
// candidate chooseBid compares, instead of each candidate's iterations
// drawing their own independent shuffle. This was added to fix a genuine
// sampling-noise problem (verified: the same hand could get 5 different
// recommendations across 15 calls before this) and is confirmed working.
//
// KNOWN LIMITATION (not fixed, tracked for future work): a hand holding a
// bare/singleton trump ace with no other cards of that suit can still be
// misjudged as a stronger trump choice than a real, longer suit that holds
// the Jack instead. The bidder is required to lead trump on trick 1 if
// holding any (see simulate.js/setbackRules.isLegalPlay's mustLeadTrump
// handling) - when that trump is a bare ace, this is their only legal card,
// so the simulation always cashes a guaranteed "High" point turn one. A
// real suit with depth+Jack has no such guarantee (High usually goes to
// whoever holds that suit's actual ace) and only wins Jack/Game
// probabilistically under the same simplified heuristic policy - so a
// certain 1 point can end up outscoring a suit that's genuinely better in
// real play. This isn't a bug in the leading heuristic itself (confirmed by
// trying to condition the ace-lead on holding backup trump - it made no
// measurable difference, since the bare-ace case never reaches that check;
// it's forced by mustLeadTrump/legalMoves before any heuristic preference
// applies) - it's a deeper EV-modeling gap: evaluateBid doesn't discount a
// guaranteed point category against categories that are only probabilistic
// under this heuristic. A real fix means rebalancing how guaranteed vs.
// probabilistic point categories are weighted here, which affects every bid
// decision, not just this hand shape - a properly scoped effort on its own,
// not a quick patch.
const evaluateBid = ({
  mySeat,
  myHand,
  level,
  trump,
  iterations = BID_ITERATIONS,
  numPlayers = 4,
  remainingPools,
}) => {
  const winningBid = { bid: level, team: seatTeam(mySeat) };
  const handSizes = {};
  // Every other seat gets a full 6-card simulated hand - except a seat
  // past numPlayers (only possible for 3-player Cutthroat's P4), which
  // doesn't exist and must get 0, or determinizeHands would fabricate a
  // full hand for a phantom opponent.
  for (const seat of SEATS) {
    const seatNum = SEATS.indexOf(seat) + 1;
    handSizes[seat] =
      seat === mySeat ? myHand.length : seatNum <= numPlayers ? 6 : 0;
  }

  const myTeam = seatTeam(mySeat);
  let total = 0;

  for (let i = 0; i < iterations; i++) {
    const hands = determinizeHands({
      mySeat,
      myHand,
      trump,
      playedCards: [],
      handSizes,
      remainingPool: remainingPools?.[i],
    });

    // The bidder is required to lead trick 1 with trump (if they hold any) -
    // simulate that constraint accurately so the EV estimate reflects it.
    let firstMoveUsed = false;
    const result = playOutHand({
      hands,
      trump,
      toActSeat: mySeat,
      winningBid,
      policy: (seat, hand, trick, trickSuit, trumpState) => {
        const mustLeadTrump = seat === mySeat && !firstMoveUsed && trick.length === 0;
        if (seat === mySeat && trick.length === 0) firstMoveUsed = true;
        return chooseHeuristicCard(hand, trick, trickSuit, trumpState, seat, mustLeadTrump);
      },
      numPlayers,
    });

    total +=
      myTeam === "teamA"
        ? result.teamAScore - result.teamBScore
        : result.teamBScore - result.teamAScore;
  }

  return total / iterations;
};

// AGGRESSIVENESS TUNING CONSTANT (2v2 Teams only - see isCutthroat below).
// Bidding higher can never raise a successful bid's score (it's capped at
// raw categories won either way) - it only raises the bar and the penalty
// for missing it - so a strict EV argmax will only ever prefer bid3 over
// bid2 when bid3 is already outright better. This margin instead prefers
// the more ambitious bid3 whenever it comes within AGGRESSION_MARGIN of
// bid2's EV, even if not the outright max, on the view that a
// close-but-nominally-lower bid3 still reflects a hand reliably winning
// most categories with a reasonable shot at one more. Scoped to the 2-vs-3
// decision only - 3-vs-4 is left a strict argmax, since "win one more of
// 4" and "win all 4" are qualitatively different asks, not the same kind
// of close call.
const AGGRESSION_MARGIN_TEAMS = 2.2;
// Cutthroat has no partner to fall back on if a bid goes wrong, so it
// keeps the original, untuned strict-EV-argmax decision rule (margin 0)
// rather than inheriting 2v2 Teams' more ambitious tuning.
const AGGRESSION_MARGIN_CUTTHROAT = 0;

// Picks the best bid (and trump suit, if bidding) for a bot's hand. Returns
// { action: "Pass" } or { action: "bid2" | "bid3" | "bid4", trump }.
// `currentWinningBid` is the numeric bid this seat must beat (0 if nobody
// has bid yet) - mirrors BiddingOptions' own bid-ladder rules exactly.
// `isCutthroat` can't be inferred from numPlayers alone - 4-player
// Cutthroat and 2v2 Teams both have numPlayers=4 - so it's passed
// separately, the same way numPlayers itself is threaded through from
// BiddingOptions.js.
const chooseBid = ({
  mySeat,
  myHand,
  currentWinningBid,
  iterations = BID_ITERATIONS,
  numPlayers = 4,
  isCutthroat = false,
}) => {
  const AGGRESSION_MARGIN = isCutthroat
    ? AGGRESSION_MARGIN_CUTTHROAT
    : AGGRESSION_MARGIN_TEAMS;
  const levels = [2, 3, 4].filter((lvl) => lvl > currentWinningBid);
  const possibleTrumps = SUITS.filter((suit) => myHand.some((c) => c.suit === suit));

  // Common random numbers: every (level, trump) candidate below is
  // evaluated against this same set of sampled opponent-hand deals, rather
  // than each candidate drawing its own independent shuffle. Two
  // candidates that are genuinely close (or clearly one better than the
  // other) can otherwise look statistically indistinguishable - or even
  // flip which one wins - purely because they happened to be compared
  // against different random opponent hands. Sharing the draws isolates
  // the comparison to what actually differs between candidates: the trump
  // suit/level itself, not luck of the deal.
  const remainingPools = Array.from({ length: iterations }, () =>
    shuffle(unseenPool({ myHand, playedCards: [] }))
  );

  const bestPerLevel = {};
  for (const level of levels) {
    let bestForLevel = null;
    for (const trump of possibleTrumps) {
      const ev = evaluateBid({ mySeat, myHand, level, trump, iterations, numPlayers, remainingPools });
      if (!bestForLevel || ev > bestForLevel.ev) bestForLevel = { ev, trump };
    }
    if (bestForLevel) bestPerLevel[level] = bestForLevel;
  }

  let best = null;
  for (const level of levels) {
    if (!bestPerLevel[level]) continue;
    if (!best || bestPerLevel[level].ev > best.ev) {
      best = { level, ...bestPerLevel[level] };
    }
  }

  // Whether to bid at all is decided by the unbiased argmax above, exactly
  // as before the aggressiveness margin existed - a hand that wouldn't
  // have cleared the bar to bid2 shouldn't be able to pass instead just
  // because bid3 (upgraded to below) happens to carry a worse EV of its
  // own. The margin only gets a say in WHICH level to bid, once bidding
  // has already been decided on its own merits.
  if (!best || best.ev <= 0) {
    return { action: "Pass" };
  }

  if (
    best.level === 2 &&
    bestPerLevel[3] &&
    bestPerLevel[3].ev >= bestPerLevel[2].ev - AGGRESSION_MARGIN
  ) {
    best = { level: 3, ...bestPerLevel[3] };
  }

  return { action: `bid${best.level}`, trump: best.trump };
};

export { chooseBid, evaluateBid };
